import React, { useState, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import { MypageContext, useMypageStore } from '../context';
import {
  ModalWrapper,
  ModalContainer,
  CloseButton,
  ModalHeader,
  ModalTitle,
  ItemInfoSection,
  ItemIcon,
  ItemName,
  ItemEffect,
  QuantitySection,
  QuantityRow,
  QuantityLabel,
  QuantityValue,
  QuantityControlGroup,
  DecrementButton,
  Count,
  IncrementButton,
  AllButton,
  TotalRow,
  TotalLabel,
  TotalValue,
  TotalItem,
  PointIcon,
  CashIcon,
  ActionButtons,
  CancelButton,
  UseButton,
} from './styled';
import { ImageUtil } from '../../../common/utils/imageUtil';
import { RegexUtil } from '../../../common/utils/regexUtil';
import useModalStore from '../../../hooks/useModalStore';

const MultiItemUseModal = observer(({ onClose, item, category }) => {
  const { mypageStore } = useMypageStore(MypageContext);
  const modalStore = useModalStore();
  const [useQuantity, setUseQuantity] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const ownedQuantity = item.count || 1;

  useEffect(() => {
    setUseQuantity(1);
  }, [item]);

  const handleDecrement = () => {
    if (useQuantity > 1) {
      setUseQuantity(useQuantity - 1);
    }
  };

  const handleIncrement = () => {
    if (useQuantity < ownedQuantity) {
      setUseQuantity(useQuantity + 1);
    }
  };

  const handleSelectAll = () => {
    setUseQuantity(ownedQuantity);
  };

  const handleUse = async () => {
    if (isLoading) return;
    
    setIsLoading(true);
    try {
      // Call API to use item with quantity
      const res = await mypageStore.actionItemUse({ item, count: useQuantity });
      if (res.code === 200) {
        // Show success message with quantity
        modalStore.openAlertModal({
          message: `${item.name} 아이템 ${useQuantity}개가 사용되었습니다.`,
        });
        
        // Refresh item list
        mypageStore.actionMyItemList({
          category: category === 'all' || category === 'using' ? '' : category,
        });
        onClose();
      }
    } catch (error) {
      console.error('Error using item:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const { image, name, desc, type, code } = item;
  
  const { totalValue, isMedal } = useMemo(() => {
    let value = 0;
    let medalType = false;

    const typeValue = type ? parseInt(type, 10) || 0 : 0;

    if (code === 'CASH_MEDAL' || code === 'MEDAL') {
      medalType = true;
      value = typeValue * useQuantity;
    } else if (code === 'POINTS') {
      medalType = false;
      value = typeValue * useQuantity;
    } else {
      // For other items, check if item has medal or point properties
      const medal = item.medal ? parseInt(item.medal, 10) || 0 : 0;
      const point = item.point ? parseInt(item.point, 10) || 0 : 0;
      
      if (medal > 0) {
        medalType = true;
        value = medal * useQuantity;
      } else {
        medalType = false;
        value = (typeValue || point) * useQuantity;
      }
    }

    return { totalValue: value, isMedal: medalType };
  }, [code, type, useQuantity, item]);

  const totalDisplay = useMemo(() => {
    const parts = [];
    
    if (totalValue > 0) {
      parts.push(
        <TotalItem key="main">
          {isMedal ? <CashIcon /> : <PointIcon />}
          <span>{RegexUtil.makeComma(totalValue)}</span>
        </TotalItem>
      );
    }
    
    if (parts.length === 0) {
      return <span>{useQuantity}개 사용</span>;
    }
    
    return parts.map((part, index) => (
      <React.Fragment key={index}>
        {part}
        {index < parts.length - 1 && <span> + </span>}
      </React.Fragment>
    ));
  }, [totalValue, isMedal, useQuantity]);

  return (
    <ModalWrapper isOpen={true} onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
          <ModalTitle>아이템 사용하기</ModalTitle>
        
        <ItemInfoSection>
          <ItemIcon src={ImageUtil.imageUrl(image, 0, 50)} alt={name} />
          {code === 'POINTS' && (
            type ? (
              <ItemName>포인트 {RegexUtil.makeComma(type)}개</ItemName>
            ) : item.point ? (
              <ItemName>포인트 {RegexUtil.makeComma(item.point)}개</ItemName>
            ) : (
              <ItemName>{name}</ItemName>
            )
          )}
          {(code === 'CASH_MEDAL') && (
            type ? (
              <ItemName>건빵 {RegexUtil.makeComma(type)}개</ItemName>
            ) : item.medal ? (
              <ItemName>건빵 {RegexUtil.makeComma(item.medal)}개</ItemName>
            ) : (
              <ItemName>{name}</ItemName>
            )
          )}
          {code !== 'POINTS' && code !== 'CASH_MEDAL' && (
            <>
              {type ? (
                <ItemName>포인트 {RegexUtil.makeComma(type)}개</ItemName>
              ) : item.point ? (
                <ItemName>포인트 {RegexUtil.makeComma(item.point)}개</ItemName>
              ) : null}
              {item.medal && (
                <ItemName>건빵 {RegexUtil.makeComma(item.medal)}개</ItemName>
              )}
              {!item.point && !type && !item.medal && (
                <>
                  <ItemName>{name}</ItemName>
                  {desc && <ItemEffect>{desc}</ItemEffect>}
                </>
              )}
            </>
          )}
        </ItemInfoSection>

        <QuantitySection>
          <QuantityRow>
            <QuantityLabel>보유갯수</QuantityLabel>
            <QuantityValue>{ownedQuantity}</QuantityValue>
          </QuantityRow>
          
          <QuantityRow>
            <QuantityLabel>사용갯수</QuantityLabel>
            <QuantityControlGroup>
              <DecrementButton
                onClick={handleDecrement}
                disabled={useQuantity <= 1}
                className="POINT minus"
              />
              <Count>
                <span>{useQuantity}</span>
              </Count>
              <IncrementButton
                onClick={handleIncrement}
                disabled={useQuantity >= ownedQuantity}
                className="POINT plus"
              />
              <AllButton onClick={handleSelectAll}>모두</AllButton>
            </QuantityControlGroup>
          </QuantityRow>

          <TotalRow>
            <TotalLabel>합계</TotalLabel>
            <TotalValue>
              {totalDisplay}
            </TotalValue>
          </TotalRow>
        </QuantitySection>

        <ActionButtons>
          <CancelButton onClick={onClose} disabled={isLoading}>취소</CancelButton>
          <UseButton onClick={handleUse} disabled={isLoading}>
            {isLoading ? '사용 중...' : '사용하기'}
          </UseButton>
        </ActionButtons>
      </ModalContainer>
    </ModalWrapper>
  );
});

export default MultiItemUseModal;

